import React, { useEffect, useState } from "react";
import RagBinTypes from "lib/constants/RagBinTypes";
import handlePopupFlagError from "lib/errors/handlePopupFlagError";
import { useDispatch, useSelector } from "store";
import { fetchRagBins, selectLocationByID, selectRagBinByID } from "store/reducers";
import { Pagination, UUID } from "types";
import { Table, TableCell, TableHeader, TableRow } from "components/Table";
import TableBody from "components/Table/TableBody";
import TableHeadCell from "components/Table/TableHeadCell";
import { TableLink } from "components/Table/TableLink/TableLink";
import TablePagination from "components/Table/TablePagination";
import TableRowNotice from "components/Table/TableRowNotice";
import { StorageStatusPill } from "components/shared/features/storages";
import { TableRowLoading } from "components/shared/tables";

type TableRowProps = {
  ragBinId: string;
};

const Row = ({ ragBinId }: TableRowProps) => {
  const entity = useSelector(selectRagBinByID(ragBinId));
  const location = useSelector(selectLocationByID(entity?.storage?.locationId || ""));
  if (!entity) return null;
  return (
    <TableRow>
      <TableCell>
        <StorageStatusPill storageId={entity?.storageId} />
      </TableCell>
      <TableCell>
        <TableLink to={`/apps/storage/rag_controllers/${entity.ragControllerId}/bins/${ragBinId}`}>
          {entity.storage?.name}
        </TableLink>
      </TableCell>
      <TableCell>{RagBinTypes.resolveItemLabel(entity.binType)}</TableCell>
      <TableCell>{location ? location.name : "N/A"}</TableCell>
    </TableRow>
  );
};

type RagBinsTableProps = {
  ragControllerId?: UUID;
};

export const RagBinsTable = ({ ragControllerId }: RagBinsTableProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [ids, setIds] = useState<UUID[]>([]);
  const [paging, setPaging] = useState<Pagination>({
    pageStart: 0,
    pageLimit: 10,
    pageCount: 0,
    totalCount: 0,
    nextPageStart: null,
    previousPageStart: null,
  });

  useEffect(() => {
    fetch(paging.pageStart, paging.pageLimit);
  }, [paging.pageStart, paging.pageLimit, ragControllerId]);

  const fetch = async (pageStart: number, pageLimit: number) => {
    try {
      setLoading(true);
      const response = await dispatch(fetchRagBins({ pageStart, pageLimit, ragControllerId })).unwrap();
      setIds(response.results.map(({ id }) => id));
      setPaging(response.paging);
    } catch (err) {
      handlePopupFlagError(err);
    }
    setLoading(false);
  };

  const prevPage = () => {
    setPaging((prevState) => ({
      ...prevState,
      pageStart: prevState.previousPageStart !== null ? prevState.previousPageStart : 0,
    }));
  };

  const nextPage = () => {
    setPaging((prevState) => ({
      ...prevState,
      pageStart: prevState.nextPageStart !== null ? prevState.nextPageStart : 0,
    }));
  };

  const handleLimitChange = (value: number) => {
    setPaging((prevState) => ({
      ...prevState,
      pageLimit: value,
    }));
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeadCell>Status</TableHeadCell>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Location</TableHeadCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!loading && ids.map((id) => <Row key={id} ragBinId={id} />)}
          {!loading && ids.length === 0 ? (
            <TableRowNotice colSpan={4} notice="You have no bins in your rag controller yet" />
          ) : null}
        </TableBody>
      </Table>
      {loading && <TableRowLoading />}
      <TablePagination paging={paging} onPrevPage={prevPage} onNextPage={nextPage} onChangeLimit={handleLimitChange} />
    </>
  );
};
